// Modified by AI on 06/12/2026. Edit #1.
import { chain, get } from 'lodash';

import React, {
    useState, useEffect, useCallback, useContext, useMemo, useRef,
} from 'react';
import { withRouter } from 'react-router-dom';
import { matchPath } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import EmployerHttpService from '../api/EmployerHttpService';
import EmployerRepHttpService from '../api/EmployerRepHttpService';
import Logger from '../services/logger';
import LoadingContext, { LOADING_AREAS } from './LoadingContext';
import Employer from '../models/Employer';
import AuthorizationContext from './AuthorizationContext';
import SettingsContext from './SettingsContext';
import { QUERY_KEYS, FIVE_MINUTES_MS } from '../constants/queryKeys';

import {
    EMPLOYER_REP_STATUSES as EmployerRepStatuses,
} from '../constants/EmployerRep';

const EmployerContext = React.createContext({
    employer: null,
});

export const EmployerContextProvider = EmployerContext.Provider;

export const EmployerContextConsumer = EmployerContext.Consumer;

// Modified by AI on 06/12/2026. Edit #2.
export const EmployerApp = withRouter(({ children, location }) => {
    const isPrintMode = location.pathname.startsWith('/print');
    const { systemPreferences } = useContext(SettingsContext);
    const { startFetchingData, stopFetchingData } = useContext(LoadingContext);
    const { employerRep } = useContext(AuthorizationContext);

    const match = matchPath(location.pathname, {
        path: `/${isPrintMode ? 'print/' : ''}employer/:employerId`,
        exact: false,
        strict: false,
    });

    const employerId = get(match, 'params.employerId', null);
    const employerIdRef = useRef(null);

    const [isCandidatesAtsIntegrationOn, setCandidatesAtsIntegration] = useState(false);

    // Modified by AI on 06/12/2026. Edit #3.
    // Fetch employer data
    const {
        data: employerData,
        error: employerError,
        isLoading: employerIsLoading,
        refetch: refetchEmployer,
    } = useQuery({
        queryKey: QUERY_KEYS.employer(employerId),
        queryFn: async () => {
            const fetchedEmployer = await EmployerHttpService.getEmployer(employerId);

            if (!fetchedEmployer && employerRep) {
                // Fallback to fetching via employerRep
                const realEmployerId = get(employerRep, 'employerId');
                if (realEmployerId) {
                    const employerViaRep = await EmployerHttpService.getEmployer(realEmployerId);
                    return employerViaRep;
                }
            }

            return fetchedEmployer;
        },
        enabled: !!employerId,
        staleTime: FIVE_MINUTES_MS,
        gcTime: FIVE_MINUTES_MS,
    });

    const employer = useMemo(() => {
        if (employerData && employerIdRef?.current === employerData?._id) {
            return new Employer(employerData);
        }
        return employerData ? new Employer(employerData) : null;
    }, [employerData]);

    const isAssociatedEmployer = !!employer?.resellerEmployerId && !employer?.settings?.isResellerPartner;

    // Modified by AI on 06/12/2026. Edit #4.
    // Fetch reseller data for associated employers
    const {
        data: resellerData,
    } = useQuery({
        queryKey: QUERY_KEYS.reseller(employerId),
        queryFn: () => EmployerHttpService.getReseller(employerId),
        enabled: !!employerId && isAssociatedEmployer,
        staleTime: FIVE_MINUTES_MS,
        gcTime: FIVE_MINUTES_MS,
    });

    // Update ATS integration flag based on employer or reseller
    useEffect(() => {
        if (employer) {
            const _isCandidatesAtsIntegrationOn = isAssociatedEmployer
                ? !!resellerData?.settings?.isCandidatesAtsIntegrationOn
                : !!employer.settings?.isCandidatesAtsIntegrationOn;

            setCandidatesAtsIntegration(_isCandidatesAtsIntegrationOn);
        }
    }, [employer, resellerData, isAssociatedEmployer]);

    // Modified by AI on 06/12/2026. Edit #5.
    // LoadingContext integration
    useEffect(() => {
        if (employerIsLoading && employerId) {
            startFetchingData(LOADING_AREAS.employer);
        }
    }, [employerIsLoading, employerId, startFetchingData]);

    useEffect(() => {
        if (!employerIsLoading && employerId) {
            stopFetchingData(LOADING_AREAS.employer);
        }
    }, [employerIsLoading, employerId, stopFetchingData]);

    // Error handling
    useEffect(() => {
        if (employerError) {
            Logger.error(employerError);
        }
    }, [employerError]);

    // Update employerIdRef
    useEffect(() => {
        if (employerId) {
            employerIdRef.current = employerId;
        }
    }, [employerId]);

    const isLowTalentPoolEnabled = !!employer?.settings?.isManualQualifyLowTalentPoolEnabled;
    const isManualSelectionEnabled = !!employer?.settings?.isManualQualifyManualFlowEnabled;

    const isManualMessagingEnabled = useMemo(() => {
        const isManualMessagingEnabledInSystem = !!systemPreferences?.isManualQualifyManualMessagingEnabled;
        const isManualMessagingEnabledInEmployerSettings = !!employer?.settings?.isManualQualifyManualMessagingEnabled;

        return isManualMessagingEnabledInSystem && isManualMessagingEnabledInEmployerSettings;
    }, [systemPreferences, employer]);

    const isRecalibrationEnabled = !!systemPreferences?.isRecalibrationFlowEnabled && !!employer?.settings?.isRecalibrationEnabled;

    const refreshEmployerData = useCallback(async () => {
        await refetchEmployer();
    }, [refetchEmployer]);

    // Modified by AI on 06/12/2026. Edit #6.
    // Preserve activateEmployerRepInOtherEmployer side effect (unchanged)
    const activateEmployerRepInOtherEmployer = useCallback(async () => {
        try {
            const employerRepsByEmail = await EmployerRepHttpService.getEmployerRepsByEmail(employerRep?.email, true, true) || [];

            const employerRepIdForActivation = chain(employerRepsByEmail)
                .find((emp) => emp?.employerId === employerId && emp?.status?.current === EmployerRepStatuses.PENDING)
                .get('_id')
                .value();

            if (employerRepIdForActivation) {
                await EmployerRepHttpService.activateExistingEmployerRep(employerRepIdForActivation).catch((error) => {
                    Logger.error(`[EmployerContext.activateEmployerRepInOtherEmployer] [employerId=${employerId}] [employerRepId=${employerRepIdForActivation}] error with activating employerRep in other employer => ${error?.message}`);
                });
            }
        } catch (error) {
            Logger.error(`[EmployerContext.activateEmployerRepInOtherEmployer] [employerId=${employerId}] [employerRepId=${employerRep?._id}] [employerRepEmail=${employerRep?.email}] error => ${error?.message}`);
        }
    }, [employerId, employerRep]);

    useEffect(() => {
        const shouldActivateEmployerRepInOtherEmployer = employerRep
            && employerRep?.employerId !== employerId
            && employerRep?.multiUser;

        if (shouldActivateEmployerRepInOtherEmployer) {
            activateEmployerRepInOtherEmployer();
        }
    }, [employerId, employerRep]);

    const contextValue = useMemo(() => ({
        employer,
        employerId,
        refreshEmployerData,
        isAtsEmployer: employer?.isAtsEmployer(),
        isPaycorEmployer: employer?.isPaycorEmployer(),
        isNeedToSkipWizardEditStep: !employer?.settings?.showManageJob,
        isNeedToSkipWizardSamplesStep: !employer?.settings?.showWizardSamplesPage,
        isAISourcingAvailable: !!employer?.settings?.isAISourcingAvailable,
        isDepartmentsEnabled: !!employer?.settings?.isDepartmentsEnabled,
        switchOnAutoCampaignOnCompleteCalibration: !!employer?.settings?.switchOnAutoCampaignOnCompleteCalibration,
        isEmployerAssociatedWithReseller: !!employer?.resellerEmployerId && !employer?.settings?.isResellerPartner,
        isResellerPartner: employer?.settings?.isResellerPartner,
        isEmployerDomainAuthenticated: !!employer?.settings?.sendersAuthenticateDomain,
        isCandidatesAtsIntegrationOn,
        isLineBreakTagInJobDescriptionEnabled: employer?.settings?.isLineBreakTagInJobDescriptionEnabled,
        atsCandidatesSyncVersion: employer?.settings?.atsCandidatesSyncVersion,
        isDiversityEnabled: employer?.isDiversityEnabled,
        isMultipleSelectionEnabled: !!employer?.settings?.isMultipleSelectionEnabled,
        isDemoEmployer: !!employer?.settings?.isDemoEmployer,
        isLowTalentPoolEnabled,
        isManualSelectionEnabled,
        isManualMessagingEnabled,
        isRecalibrationEnabled,
    }), [
        employer,
        employerId,
        refreshEmployerData,
        isManualSelectionEnabled,
        isManualMessagingEnabled,
        isRecalibrationEnabled,
        isCandidatesAtsIntegrationOn,
    ]);

    return (
        <EmployerContextProvider value={contextValue}>
            {children}
        </EmployerContextProvider>
    );
});

export default EmployerContext;
